import React, { useEffect, useState, forwardRef, useImperativeHandle, useRef } from 'react';
import { Pencil, Trash2, AlertCircle, X, CheckCircle } from 'lucide-react';
import { getHVWorkPermits, deleteHVWorkPermit } from '../../../services/hvWorkPermitService';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Pagination from '../../../components/Pagination/Pagination';

interface WorkRequest {
  id: number;
  refNo: string;
  createdDate: string;
  department: string;
  priority: string;
  location: string;
  status: string;
  statusColor: string;
}

const WorkRequestTable = forwardRef((props, ref) => {
  const navigate = useNavigate();
  const role = useSelector((state: any) => state.user?.role);
  const isResponsiblePerson = role === 'responsible_person';
  const [workPermits, setWorkPermits] = useState<WorkRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState('');
  const [showError, setShowError] = useState(false);

  // Success State
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Delete modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [searchQuery, setSearchQuery] = useState('');

  // useEffect(() => {
  //   fetchWorkPermits(currentPage);
  // }, [currentPage]);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const delay = searchQuery ? 150 : 0;

    debounceRef.current = setTimeout(() => {
      fetchWorkPermits(currentPage, searchQuery);
    }, delay);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [currentPage, searchQuery]);

  // CHANGED: added search param
  const fetchWorkPermits = async (page = 1, search = '') => {
    try {
      if (initialLoad) {
        setLoading(true);
      }
      const response = await getHVWorkPermits(page, search); // CHANGED: pass search

      // setCurrentPage(response?.meta?.current_page || 1);
      if (response?.meta?.current_page !== currentPage) {
        setCurrentPage(response?.meta?.current_page || 1);
      }
      setTotalPages(response?.meta?.last_page || 1);

      const transformedData = response.data.map((permit: any) => ({
        id: permit.id,
        refNo: permit.ref_no || `#WRP${permit.id.toString().padStart(9, '0')}`,
        createdDate: permit.created_at ? new Date(permit.created_at).toLocaleDateString('en-GB') : '-',
        department: permit.department || '-',
        priority: permit.priority || '-',
        location: permit.location || '-',
        status: permit.status || 'New',
        statusColor: getStatusColor(permit.status || 'New'),
      }));

      setWorkPermits(transformedData);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to load work permits.';
      setError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  useImperativeHandle(ref, () => ({
    fetchWorkPermits, //  exposes updated fetchWorkPermits with search support
  }));

  const getStatusColor = (status: string) => {
    const s = status.toLowerCase();
    if (s === 'new' || s === 'pending') return 'text-purple-600';
    if (s === 'isolation pending') return 'text-orange-500';
    if (s === 'permit taken out') return 'text-purple-500';
    if (s === 'issue permit pending') return 'text-orange-500';
    if (s === 'permit issued') return 'text-pink-600';
    if (s === 'rp change pending') return 'text-orange-600';
    if (s === 'rp changed') return 'text-green-600';
    if (s === 'clearance done') return 'text-green-500';
    if (s === 'clearance pending') return 'text-purple-500';
    if (s === 'isolation effected' || s === 'in progress') return 'text-orange-500';
    if (s === 'clearance done (ao)' || s === 'completed' || s === 'approved') return 'text-green-600';
    return 'text-gray-600';
  };

  const getStatusDotColor = (statusColor: string) => {
    if (statusColor.includes('purple')) return 'bg-purple-600';
    if (statusColor.includes('orange')) return 'bg-orange-500';
    if (statusColor.includes('green')) return 'bg-green-600';
    if (statusColor.includes('pink')) return 'bg-pink-600';
    return 'bg-gray-600';
  };

  const canEdit = (status: string) => {
    const editableStatuses = ['new', 'isolation pending'];
    return editableStatuses.includes(status.toLowerCase());
  };
  // Handle Edit - Navigate to same page with edit param
  const handleEdit = (id: number) => {
    navigate(`/HVworkPermits?edit=${id}`);
  };

  const openDeleteModal = (id: number) => {
    setSelectedId(id);
    setShowDeleteModal(true);
  };

  // Confirm Delete
  const confirmDelete = async () => {
    if (selectedId === null) return;

    setIsDeleting(true);

    try {
      await deleteHVWorkPermit(selectedId);

      setShowDeleteModal(false);
      setSelectedId(null);

      // Show Success Message
      setSuccessMessage('Work permit successfully deleted.');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 4000);

      fetchWorkPermits(currentPage);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to delete work permit.';
      setError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-600">Loading work permits...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
        <table className="min-w-full text-sm text-left border rounded shadow">
          <thead className="text-gray-600 text-xs">
            <tr className="h-[45px]">
              <th className="px-4 py-1 border text-xs font-medium text-gray-500">Sl. No</th>
              <th className="px-4 py-1 border text-xs font-medium text-gray-500">Ref No.</th>
              <th className="px-4 py-1 border text-xs font-medium text-gray-500">Created Date</th>
              <th className="px-4 py-1 border text-xs font-medium text-gray-500">Department</th>
              <th className="px-4 py-1 border text-xs font-medium text-gray-500">Priority</th>
              <th className="px-4 py-1 border text-xs font-medium text-gray-500">Location</th>
              <th className="px-4 py-1 border text-xs font-medium text-gray-500">Status</th>
              {isResponsiblePerson && <th className="px-4 py-1 border text-xs font-medium text-gray-500">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {workPermits.length === 0 ? (
              <tr>
                <td colSpan={isResponsiblePerson ? 8 : 7} className="px-4 py-8 text-center text-gray-500">
                  No work permits found.
                </td>
              </tr>
            ) : (
              workPermits.map((item, index) => (
                <tr
                  key={item.id}
                  className="border hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => {
                    navigate(`/HVworkPermits/${item.id}`);
                  }}
                >
                  <td className="px-4 py-3 text-gray-500 border text-center">{(currentPage - 1) * 10 + index + 1}</td>
                  <td className="px-4 py-3 text-black-700 font-medium border">{item.refNo}</td>
                  <td className="px-4 py-3 border font-medium">{item.createdDate}</td>
                  <td className="px-4 py-3 border font-medium">{item.department}</td>
                  <td className="px-4 py-3 border font-medium">{item.priority}</td>
                  <td className="px-4 py-3 border font-medium">{item.location}</td>
                  <td className={`px-4 py-3 font-medium ${item.statusColor} border`}>
                    <span className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${getStatusDotColor(item.statusColor)}`}></span>
                      {item.status}
                    </span>
                  </td>
                  {isResponsiblePerson && (
                    <td className="px-4 py-3 border">
                      {canEdit(item.status) ? (
                        <div className="flex gap-3 text-gray-600">
                          <button
                            className="text-blue-600"
                            title="Edit"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(item.id);
                            }}
                          >
                            <Pencil size={18} />
                          </button>
                          <button
                            className="text-red-600"
                            title="Delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteModal(item.id);
                            }}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

      {/* DELETE MODAL */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white w-[420px] rounded-xl shadow-xl p-6 relative">
            <button onClick={() => setShowDeleteModal(false)} className="absolute top-3 right-3 text-gray-500 hover:text-gray-800">
              <X size={20} />
            </button>

            <h2 className="text-lg font-semibold text-center mb-6">Are you sure you want to delete this work permit?</h2>

            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
                className="px-6 py-2 border rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>

              <button
                onClick={confirmDelete}
                disabled={isDeleting}
                className="px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:bg-red-300 disabled:cursor-not-allowed"
              >
                {isDeleting ? 'Deleting..' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SUCCESS NOTIFICATION */}
      {showSuccess && (
        <div className="fixed bottom-6 right-6 z-[100]">
          <div className="bg-green-500 text-white rounded-lg shadow-lg p-4 pr-12 min-w-[300px] max-w-[500px] relative">
            <button onClick={() => setShowSuccess(false)} className="absolute top-3 right-3 text-white hover:text-gray-200">
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold mb-1">Success</h4>
                <p className="text-sm">{successMessage}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ERROR NOTIFICATION */}
      {showError && (
        <div className="fixed bottom-6 right-6 z-[100]">
          <div className="bg-red-500 text-white rounded-lg shadow-lg p-4 pr-12 min-w-[300px] max-w-[500px] relative">
            <button onClick={() => setShowError(false)} className="absolute top-3 right-3 text-white hover:text-gray-200">
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold mb-1">Error</h4>
                <p className="text-sm">{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

WorkRequestTable.displayName = 'WorkRequestTable';
export default WorkRequestTable;
